import { BookerByIdQuery, Multilingual, Product } from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { MultilingualLangKey } from "shared/types";

const isMultilingualEmpty = (multilingual?: Multilingual | null) =>
  !multilingual ||
  (!multilingual.en?.length && !multilingual.es?.length && !multilingual.fr?.length);

/**
 * Widget-only override for the product name shown to customers. Falls back to
 * the product's real name when no override is set, so this must never be used
 * anywhere the underlying product record is read/written (cart, emails, admin).
 */
export const getOverriddenProductName = (
  product: Product | null | undefined,
  booker: BookerByIdQuery["bookerById"] | null | undefined,
  language?: MultilingualLangKey,
): string => {
  const override = booker?.customizations?.overrideProductName;
  return !isMultilingualEmpty(override)
    ? getMultilingualString(override as Multilingual, language)
    : getMultilingualString(product?.name ?? {}, language);
};

/**
 * Widget-only override for the product description shown to customers. Falls
 * back to the product's real description when no override is set.
 */
export const getOverriddenProductDescription = (
  product: Product | null | undefined,
  booker: BookerByIdQuery["bookerById"] | null | undefined,
  language?: MultilingualLangKey,
): string => {
  const override = booker?.customizations?.overrideProductDescription;
  return !isMultilingualEmpty(override)
    ? getMultilingualString(override as Multilingual, language)
    : getMultilingualString(product?.description ?? {}, language);
};

/**
 * Widget-only override for the "Starting from $X" style pricing callout tag.
 * Falls back to the computed pricing text when no override is set.
 */
export const getOverriddenPricingCallout = (
  booker: BookerByIdQuery["bookerById"] | null | undefined,
  computedPricingText: string,
  language?: MultilingualLangKey,
): string => {
  const override = booker?.customizations?.overridePricingCallout;
  return !isMultilingualEmpty(override)
    ? getMultilingualString(override as Multilingual, language)
    : computedPricingText;
};

/**
 * Widget-only override for the active-discount callout tag(s). When set, it
 * replaces the individually computed discount tags with a single custom one.
 */
export const getOverriddenDiscountCallout = (
  booker: BookerByIdQuery["bookerById"] | null | undefined,
  language?: MultilingualLangKey,
): string | null => {
  const override = booker?.customizations?.overrideDiscountCallout;
  return !isMultilingualEmpty(override)
    ? getMultilingualString(override as Multilingual, language)
    : null;
};

/**
 * Widget-only replacement for the add-ons step's subtitle. Falls back to the
 * default copy when no override is set.
 */
export const getOverriddenAddOnsDescription = (
  booker: BookerByIdQuery["bookerById"] | null | undefined,
  defaultText: string,
  language?: MultilingualLangKey,
): string => {
  const override = booker?.customizations?.overrideAddOnsDescription;
  return !isMultilingualEmpty(override)
    ? getMultilingualString(override as Multilingual, language)
    : defaultText;
};

/**
 * Widget-only heading for the questionnaire section. Falls back to the
 * questionnaire's real name, which may be empty. The questionnaire card and
 * page keep showing the real name.
 */
export const getOverriddenQuestionnaireTitle = (
  booker: BookerByIdQuery["bookerById"] | null | undefined,
  language?: MultilingualLangKey,
): string => {
  const override = booker?.customizations?.overrideQuestionnaireTitle;
  return !isMultilingualEmpty(override)
    ? getMultilingualString(override as Multilingual, language)
    : getMultilingualString(booker?.survey?.name ?? {}, language);
};

/**
 * Widget-only replacement for the questionnaire step's subtitle. Falls back to
 * the default copy when no override is set.
 */
export const getOverriddenQuestionnaireDescription = (
  booker: BookerByIdQuery["bookerById"] | null | undefined,
  defaultText: string,
  language?: MultilingualLangKey,
): string => {
  const override = booker?.customizations?.overrideQuestionnaireDescription;
  return !isMultilingualEmpty(override)
    ? getMultilingualString(override as Multilingual, language)
    : defaultText;
};
